import { useCallback } from 'react';
import {
  SchedulerResourceType,
  TeamAsLeafResource,
  UserAsLeafResource,
} from '../resource-types';
import { GroupBy, SchedulerReference } from '../types';
import { resourceIdForProject } from '../utils/id';

interface Props {
  groupBy: GroupBy;
  schedulerRef: SchedulerReference;
}
interface Result {
  enableAutoAllocateForProject: (projectId: number) => void;
  disableAutoAllocateForProject: (projectId: number) => void;
}
export const useAutoAllocationStateManager = ({
  groupBy,
  schedulerRef,
}: Props): Result => {
  const changeAutoAllocateStateForGroupByProject = useCallback(
    (projectId: number, readOnly: boolean) => {
      const resource =
        schedulerRef.current.instance.project.resourceStore.getById(
          resourceIdForProject(projectId),
        );

      resource.set('readOnly', readOnly);
      resource.set('autoAllocation', readOnly);

      resource.children.forEach((child) => {
        const { type, children: roleChildren } = child;

        if (type === SchedulerResourceType.AssignUserOrPlaceholder) {
          child.set('readOnly', readOnly);
          child.set('autoAllocation', readOnly);
        } else if (type === SchedulerResourceType.Role) {
          (
            (roleChildren ?? []) as (UserAsLeafResource | TeamAsLeafResource)[]
          ).forEach((userOrTeamResource) => {
            const leafResource =
              schedulerRef.current.instance.project.resourceStore.getById(
                userOrTeamResource.id,
              );

            if (leafResource != null) {
              leafResource.set('readOnly', readOnly);
              leafResource.set('autoAllocation', readOnly);
            }
          });
        }
      });
    },
    [schedulerRef],
  );
  const changeAutoAllocateStateForGroupByUser = useCallback(
    (projectId: number, readOnly: boolean) => {
      const allResources =
        schedulerRef.current.instance.resourceStore.allRecords;

      allResources.forEach((resource) => {
        if (resource.get('projectId') === projectId) {
          resource.set('readOnly', readOnly);
          resource.set('autoAllocation', readOnly);
        }
      });
    },
    [schedulerRef],
  );
  const enableAutoAllocateForProject = useCallback(
    (projectId: number) => {
      if (groupBy === GroupBy.Projects) {
        changeAutoAllocateStateForGroupByProject(projectId, true);
      } else {
        changeAutoAllocateStateForGroupByUser(projectId, true);
      }
    },
    [
      changeAutoAllocateStateForGroupByProject,
      changeAutoAllocateStateForGroupByUser,
      groupBy,
    ],
  );
  const disableAutoAllocateForProject = useCallback(
    (projectId: number) => {
      if (groupBy === GroupBy.Projects) {
        changeAutoAllocateStateForGroupByProject(projectId, false);
      } else {
        changeAutoAllocateStateForGroupByUser(projectId, false);
      }
    },
    [
      changeAutoAllocateStateForGroupByProject,
      changeAutoAllocateStateForGroupByUser,
      groupBy,
    ],
  );

  return {
    enableAutoAllocateForProject,
    disableAutoAllocateForProject,
  };
};
